import { isEqual } from "lodash-es";
import { type QInfiniteScroll } from "quasar";
import {
  computed,
  inject,
  nextTick,
  onActivated,
  onDeactivated,
  onMounted,
  onUnmounted,
  ref,
  toRef,
  watch,
} from "vue";
import { type ComponentExposed } from "vue-component-type-helpers";
import { useRoute, useRouter } from "vue-router";

import { track } from "shared/boot/analytics";
import { getLocaleText } from "shared/boot/i18n";
import useRequestMentions from "shared/composables/useRequestMentions";
import useStreamFiltering from "shared/composables/useStreamFiltering";
import {
  DAY_IN_MILLISECONDS,
  STREAM_EXTENDED_EXPIRY_DAYS,
} from "shared/constants";
import { formatIntlDate } from "shared/helpers/date";
import DateRange from "shared/helpers/DateRange";
import { safePush } from "shared/helpers/routing";
import { expiryHasExtended, isSameStream } from "shared/helpers/stream";
import type StreamFilters from "shared/helpers/StreamFilters";
import DialogService from "shared/modals/DialogService";
import {
  type MentionStreamEmits,
  type MentionStreamProps,
} from "shared/modules/MentionStream/types";
import features from "shared/services/features";
import useStreamsStore from "shared/stores/streams";
import { type FilterSet, type Maybe, type Nullable } from "shared/types";
import { type Mention } from "shared/types/mentions";

export function useMentionStream(
  props: MentionStreamProps,
  { emit }: { emit: MentionStreamEmits }
) {
  const isDesktop = inject<boolean>("isDesktop");

  const route = useRoute();
  const router = useRouter();
  const safeRouterPush = safePush({ router });

  const infiniteScroll = ref<Maybe<ComponentExposed<typeof QInfiniteScroll>>>();

  const isActive = ref(false);
  const navBarHandler = ref<Nullable<{ remove: () => void }>>(null);
  const range = ref(props.defaultRange || DateRange.today());
  const isRangeSelectorActive = ref(false);
  const hasReachedOneYear = ref(false);
  const scrollPosition = ref(props.initialScrollPosition || 0);
  const isSearchingRange = ref(false);
  const spanRange = ref(0);

  const {
    handlePaginationChange,
    hasReachedEndOfMentions,
    lastRefreshedRange,
    loading,
    loadingError,
    loadMentions,
    loadNextPage,
    loadPreviousPage,
    mentions,
    page,
    pageSize,
    rangeLimit,
    refresh,
    setFilters,
    setFilterSetId,
    totalPages,
  } = useRequestMentions({
    defaultRange: toRef(() => props.defaultRange),
    fetchingOptions: toRef(() => props.fetchingOptions),
    fetchingService: toRef(() => props.fetchingService),
    infiniteScroll,
    paginate: true,
    stream: toRef(() => props.stream),
  });

  const {
    displayFiltersSelector,
    filtering,
    filteringLabel,
    filters,
    filterSet,
    filtersSelectorActive,
    selectorActive,
  } = useStreamFiltering({
    ...(props.streamFilters ? { filters: props.streamFilters } : {}),
    stream: props.stream,
  });

  const streamsStore = useStreamsStore();
  const { setStreamFilters, setStreamFilterSet } = streamsStore;

  const isSkeletonMentionStreamVisible = computed(
    () => loading.value && !mentions.value.length
  );

  const isNoRangeSelectedMessageVisible = computed(
    () => !isSearchingRange.value && Boolean(props.showMoreContentOption)
  );

  const infiniteScrollClass = computed(() => [
    props.scrollClass,
    isDesktop ? "scroll q-pb-xs tw:min-h-[280px]" : "",
  ]);

  const streamTitle = computed(() => {
    if (props.title) return props.title;

    return props.stream?.label || "";
  });

  const isNotificationWatcherVisible = computed(() => {
    if (props.hideNotificationWatcher) return false;

    return Boolean(props.stream) && !filtering.value;
  });

  const isStreamExpiringSoon = computed(() => {
    const expiresAt = props.stream?.expires_at;

    if (!expiresAt) return false;

    const dateDiff = new Date(expiresAt).getTime() - new Date().getTime();

    return (
      !props.stream?.expiration_dismissed &&
      dateDiff > 0 &&
      dateDiff <= DAY_IN_MILLISECONDS
    );
  });

  const providedFilters = computed(() => filters.value);

  const slimViewOptions = computed(() => {
    if (props.slimView) {
      return {
        disableAuthorClick: true,
        hideAfterBodySlot: true,
        hideExcerpts: props.hideExcerpts,
        noSquareBorders: true,
        sentimentRatingDisabled: true,
      };
    }

    return {
      noSquareTopBorder: props.noSquareTopBorder,
    };
  });

  const isAISummarisationAllowed = computed(
    () =>
      Boolean(features.has("has_ai_summarisation")) &&
      Boolean(props.stream?.id) &&
      !props.onNavBar &&
      !props.hideButtonBar
  );

  const pagination = computed(() => ({
    page: page.value,
    pageCount: totalPages.value,
    pageSize: pageSize.value,
  }));

  const updateScrollPosition = ($event: Event) => {
    const target = $event.target as HTMLElement;
    if (!target) return;
    scrollPosition.value = target.scrollTop;
    emit("update:scrollPosition", scrollPosition.value);
  };

  const toggleScrollListener = (enable: boolean) => {
    if (!infiniteScroll.value) return;

    const method = enable ? "addEventListener" : "removeEventListener";

    infiniteScroll.value.$el[method]("scroll", updateScrollPosition);
  };

  const loadMore = async (_index: number, done: () => void) => {
    if (!mentions.value.length) {
      infiniteScroll.value?.stop();
      done();

      return;
    }

    if (loading.value) {
      done();

      return;
    }

    setFilters({
      ...filters.value.requestFilters(),
      ...(lastRefreshedRange.value ? { range: lastRefreshedRange.value } : {}),
    });

    setFilterSetId(filterSet.value?.id || null);

    await loadNextPage({
      keepAlreadyLoadedMentions: Boolean(mentions.value.length),
    });

    done();
  };

  const onRangeUpdated = (nextRange: DateRange) => {
    refresh({ range: nextRange });
  };

  const mentionClicked = (mention: Mention) => {
    emit("mention-clicked", mention);
  };

  const extendRange = () => {
    if (!lastRefreshedRange.value) return;

    let { after, before } = lastRefreshedRange.value;

    after -= spanRange.value;
    after = Math.max(after, rangeLimit.value);
    before -= spanRange.value;

    const nextRange = new DateRange({ after, before });

    range.value = nextRange;
    infiniteScroll.value?.resume();

    filters.value.range = new DateRange({
      after,
      before: filters.value.initialFilters.range?.before,
    });

    page.value = 1;
    setFilters(filters.value.requestFilters());
    setFilterSetId(filterSet.value?.id || null);

    loadMentions({
      keepAlreadyLoadedMentions: true,
      page: page.value,
      range: nextRange,
    });

    if (after === rangeLimit.value) hasReachedOneYear.value = true;
  };

  const openEditStream = () => {
    track("Opened Edit Mention Stream", {
      streamId: props.stream.id,
    });

    if (props.onNavBar && route.params.streamSlug !== props.stream.slug) {
      return;
    }

    safeRouterPush({
      name: "mention-stream-edit",
      params: {
        groupSlug: props.groupSlug,
        streamSlug: props.stream.slug,
      },
    });
  };

  const refreshMentions = async (
    {
      shouldTrackUniversalSearch = false,
      timestamp = null,
    }: {
      shouldTrackUniversalSearch?: boolean;
      timestamp?: Nullable<number>;
    } = {},
    done: () => void = () => {}
  ) => {
    if (infiniteScroll.value) {
      infiniteScroll.value.$el.scrollTop = 0;
    }

    page.value = 1;

    if (timestamp) {
      range.value = new DateRange({
        ...range.value,
        before: timestamp,
      });
    } else {
      range.value = props.defaultRange || DateRange.today();
    }

    await loadMentions({
      clearMentionsCount: true,
      forceReload: true,
      page: page.value,
      range: range.value,
      ...(shouldTrackUniversalSearch
        ? { shouldTrackUniversalSearch: true }
        : {}),
    });

    emit("refreshed");
    done();
  };

  const autoRefreshMentions = () => {
    if (loading.value) return;

    // Don't yank the list out from under the user while they're reading
    // further down. The next count poll re-emits, so this retries once
    // they're back at the top rather than disabling auto-refresh.
    if ((infiniteScroll.value?.$el.scrollTop ?? 0) > 0) return;

    refreshMentions();
  };

  const activateSelector = (selector: string) => {
    if (selectorActive.value === selector) {
      selectorActive.value = null;

      track("Closed Mention Stream Filters", {
        streamId: props.stream.id,
      });
    } else {
      selectorActive.value = selector;

      track("Opened Mention Stream Filters", {
        streamId: props.stream.id,
      });
    }
  };

  const onFilter = (nextFilters: StreamFilters) => {
    if (!filters.value) return;

    filters.value.collapseSyndicated = nextFilters?.collapseSyndicated ?? true;
    isSearchingRange.value = false;
    const requestFilters = filters.value.requestFilters();
    if (requestFilters.range) range.value = nextFilters.range;

    hasReachedOneYear.value = false;

    if (requestFilters.range || filters.value.filteringSortOptions()) {
      isSearchingRange.value = true;
    }

    setFilters(requestFilters);
    setFilterSetId(filterSet.value?.id || null);
    page.value = 1;

    loadMentions({
      clearMentionsCount: false,
      keepAlreadyLoadedMentions: false,
      page: page.value,
      range: range.value,
    });
  };

  const onFilterReset = (nextFilters: StreamFilters) => {
    track("Clicked Stream Search Reset", {
      streamId: props.stream.id,
    });

    filters.value = nextFilters;

    if (props.onNavBar && props.navBar) {
      props.navBar.bus.$emit("navbar-button-clicked", "filter");
    }

    selectorActive.value = null;
    isSearchingRange.value = false;
    range.value = props.defaultRange || DateRange.today();

    if (filters.value) {
      filters.value.range = range.value;
      filters.value.dateRangeEnabled = false;
      filters.value.collapseSyndicated = true;
    }

    filterSet.value = null;
    setFilterSetId(null);

    page.value = 1;

    if (filters.value) {
      setFilters(filters.value.requestFilters());
    }

    loadMentions({
      clearMentionsCount: false,
      keepAlreadyLoadedMentions: false,
      page: page.value,
    });

    setStreamFilters({ filters: null, streamId: props.stream.id });

    setStreamFilterSet({
      filterSet: null,
      streamId: props.stream.id,
    });
  };

  const onFilterSetApply = (nextFilterSet: Nullable<FilterSet>) => {
    filterSet.value = nextFilterSet;
    setFilterSetId(nextFilterSet?.id || null);
  };

  const onFilterApply = (nextFilters: StreamFilters) => {
    if (props.onNavBar && props.navBar) {
      props.navBar.bus.$emit("navbar-button-clicked", "filter");
    }

    selectorActive.value = null;

    filters.value = nextFilters;

    if (nextFilters.dateRangeEnabled) {
      range.value = nextFilters.range;
    }

    onFilter(nextFilters);

    setStreamFilters({
      filters: filters.value,
      streamId: props.stream.id,
    });

    setStreamFilterSet({
      filterSet: filterSet.value,
      streamId: props.stream.id,
    });
  };

  const onLoadingErrorRetry = () => {
    track("Retry Loading Mention Stream After Error", {
      streamId: props.stream.id,
    });

    page.value = 1;

    loadMentions({
      clearMentionsCount: false,
      page: page.value,
      range: range.value,
    });
  };

  const handleOpenContactSidebar = (mention: Mention, componentId?: string) => {
    emit("contact-clicked", mention, componentId);
  };

  const removeMention = (mention: Mention) => {
    track("Removed from Mention Stream", {
      id: mention.id,
      streamId: props.stream.id,
      type: mention.type,
    });

    nextTick(() => mentions.value.splice(mentions.value.indexOf(mention), 1));
  };

  const onMentionSelected = (mention: Mention) => {
    emit("mention-selected", mention);
  };

  watch(
    () => props.stream,
    (newStream, oldStream) => {
      if (newStream.skipReload) return;

      if (isSameStream(newStream, oldStream)) {
        if (
          isActive.value &&
          expiryHasExtended(newStream, oldStream, STREAM_EXTENDED_EXPIRY_DAYS)
        ) {
          DialogService.alert({
            message: getLocaleText("mention_stream.expiry_extended_message", {
              streamLabel: props.stream.label,
            }),
            title: getLocaleText("mention_stream.expiry_extended_title"),
          });
        }

        if (props.stream.onlyLastSeenUpdated && !props.stream.resetByGroup) {
          return;
        }
      }

      refresh();
    },
    { deep: true }
  );

  watch(
    range,
    () => {
      emit("update:range", range.value);
    },
    { immediate: false }
  );

  watch(
    () => props.streamFilters,
    (newFilters, oldFilters) => {
      if (isEqual(newFilters, oldFilters)) return;

      if (props.streamFilters) {
        filters.value = props.streamFilters;
        onFilter(filters.value);
      }
    }
  );

  onMounted(async () => {
    spanRange.value = range.value.span;

    toggleScrollListener(true);

    if (props.onNavBar && props.navBar) {
      if (!props.hideFiltersSelector) {
        props.navBar.buttonClicked("filter", () => {
          if (!isActive.value) return false;
          activateSelector("filters");

          return true;
        });
      }

      if (props.editMention) {
        navBarHandler.value = props.navBar.buttonClicked(
          "edit-mention-stream",
          () => {
            openEditStream();
          }
        );
      }
    }

    isActive.value = true;

    if (!props.streamFilters && filters.value) {
      filters.value.collapseSyndicated = true;
    }

    if (props.streamFilters?.dateRangeEnabled) {
      filters.value.dateRangeEnabled = true;
      filters.value.range = range.value;
    }

    if (filters.value.dateRangeEnabled) {
      isSearchingRange.value = true;
    }

    page.value = 1;
    setFilters(filters.value.requestFilters());
    setFilterSetId(filterSet.value?.id || null);

    await loadMentions({ page: page.value });

    if (props.showMoreContentOption && lastRefreshedRange.value) {
      range.value = new DateRange(lastRefreshedRange.value);
    }
  });

  onActivated(() => {
    isActive.value = true;
    selectorActive.value = null;

    toggleScrollListener(true);

    nextTick(() => {
      if (!infiniteScroll.value) return;
      infiniteScroll.value.$el.scrollTop = scrollPosition.value;
    });
  });

  onDeactivated(() => {
    toggleScrollListener(false);

    isActive.value = false;
  });

  onUnmounted(() => {
    toggleScrollListener(false);

    navBarHandler.value?.remove();
  });

  return {
    activateSelector,
    autoRefreshMentions,
    displayFiltersSelector,
    extendRange,
    filtering,
    filteringLabel,
    filters,
    filterSet,
    filtersSelectorActive,
    formatIntlDate,
    handleOpenContactSidebar,
    handlePaginationChange,
    hasReachedEndOfMentions,
    hasReachedOneYear,
    infiniteScroll,
    infiniteScrollClass,
    isActive,
    isAISummarisationAllowed,
    isNoRangeSelectedMessageVisible,
    isNotificationWatcherVisible,
    isRangeSelectorActive,
    isSearchingRange,
    isSkeletonMentionStreamVisible,
    isStreamExpiringSoon,
    loading,
    loadingError,
    loadMentions,
    loadMore,
    loadNextPage,
    loadPreviousPage,
    mentionClicked,
    mentions,
    onFilterApply,
    onFilterReset,
    onFilterSetApply,
    onLoadingErrorRetry,
    onMentionSelected,
    onRangeUpdated,
    page,
    pagination,
    providedFilters,
    range,
    refresh,
    refreshMentions,
    removeMention,
    slimViewOptions,
    streamTitle,
    totalPages,
  };
}
